/**
 * fontManager - CJK font loading + locale-aware font getters
 * Loads Noto Sans SC/JP/KR from Google Fonts on demand for CJK locales.
 */

import type { Locale } from './I18nService';

const FONT_URLS: Partial<Record<Locale, { family: string; url: string }>> = {
  'zh-CN': {
    family: 'Noto Sans SC',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700;900&display=swap',
  },
  'ja': {
    family: 'Noto Sans JP',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700;900&display=swap',
  },
  'ko': {
    family: 'Noto Sans KR',
    url: 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700;900&display=swap',
  },
};

let loadedLocale: Locale | null = null;
let cjkFontFamily: string | null = null;

/**
 * Load a CJK font from Google Fonts. Idempotent per locale.
 */
export function loadCJKFont(locale: Locale): void {
  if (loadedLocale === locale) return;
  const fontDef = FONT_URLS[locale];
  if (!fontDef) return;

  // Insert <link> into <head>
  const linkId = `cjk-font-${locale}`;
  if (!document.getElementById(linkId)) {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = fontDef.url;
    document.head.appendChild(link);
  }

  loadedLocale = locale;
  cjkFontFamily = fontDef.family;
  console.log(`[FontManager] Loading CJK font: ${fontDef.family}`);
}

/**
 * Get the heading font string for the current locale.
 * Returns Orbitron for Latin scripts, Noto Sans XX for CJK.
 */
export function getHeadingFont(): string {
  if (cjkFontFamily) {
    return `'${cjkFontFamily}', 'Orbitron', 'Arial Black', sans-serif`;
  }
  return "'Orbitron', 'Arial Black', sans-serif";
}

/**
 * Get the body font string for the current locale.
 * Returns Share Tech Mono for Latin scripts, Noto Sans XX for CJK.
 */
export function getBodyFont(): string {
  if (cjkFontFamily) {
    return `'${cjkFontFamily}', 'Share Tech Mono', 'Courier New', monospace`;
  }
  return "'Share Tech Mono', 'Courier New', monospace";
}

/**
 * Get the UI font string.
 */
export function getUIFont(): string {
  if (cjkFontFamily) {
    return `'${cjkFontFamily}', Arial, Helvetica, sans-serif`;
  }
  return 'Arial, Helvetica, sans-serif';
}

/**
 * Reset font state (e.g., when switching to a non-CJK locale).
 */
export function resetCJKFont(): void {
  cjkFontFamily = null;
  loadedLocale = null;
}
